import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LoginForm from './components/auth/LoginForm';
import Layout from './components/layout/Layout';
import Dashboard from './components/dashboard/Dashboard';
import OrdersPage from './components/orders/OrdersPage';
import ReferralsPage from './components/referrals/ReferralsPage';
import PartnerBotsPage from './components/partner-bots/PartnerBotsPage';
import PartnerBotDetailPage from './components/partner-bots/PartnerBotDetailPage';
import PricesPage from './components/prices/PricesPage';
import DigitalGoodsPage from './components/digital-goods/DigitalGoodsPage';
import BotSettingsPage from './components/bot-settings/BotSettingsPage';
import ReviewsPage from './components/reviews/ReviewsPage';
import BroadcastsPage from './components/broadcasts/BroadcastsPage';
import WithdrawalsPage from './components/withdrawals/WithdrawalsPage';
import Toast from './components/common/Toast';
import NotFoundPage from './components/NotFoundPage.jsx';
import LogsPage from './components/logs/LogsPage';
import MetricsPage from './components/metrics/MetricsPage';
import FrogPage from './components/frog/FrogPage';
import AnalyticsPage from './components/analytics/AnalyticsPage';
import UserProfilePage from './components/users/UserProfilePage';
import AdminUsersPage from './components/users/AdminUsersPage';
import RolesPage from './components/users/RolesPage';
import ClientsPage from './components/users/ClientsPage';
import AntiFraudPage from './components/anti-fraud/AntiFraudPage';
import SiteContentPage from './components/site-content/SiteContentPage';
import LoyaltySettingsPage from './components/loyalty/LoyaltySettingsPage';
import { API_BASE_URL } from './config';

/**
 * Проверка валидности токена через API
 * 
 * Поскольку куки установлены с флагом httponly=True, они недоступны через document.cookie
 * в JavaScript. Поэтому проверяем авторизацию напрямую через API запрос.
 * 
 * Куки автоматически отправляются с запросом благодаря credentials: 'include'
 * 
 * @returns {Promise<boolean>} true если токен валиден, false если нет
 */
const verifyAuth = async () => {
    try {
        console.log('🔍 Проверка авторизации через API...');
        const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
            method: 'GET',
            credentials: 'include', // Важно! Отправляет httponly cookies автоматически
            headers: {
                'Content-Type': 'application/json',
            },
        });
        
        console.log('📡 Ответ от /api/auth/me:', response.status, response.statusText);
        
        if (response.ok) {
            const userData = await response.json();
            console.log('✅ Авторизация успешна:', userData);
            return true;
        } else {
            const errorData = await response.json().catch(() => ({}));
            console.log('❌ Авторизация не прошла:', errorData);
            return false;
        }
    } catch (error) {
        console.error('❌ Ошибка при проверке авторизации:', error);
        return false;
    }
};

// Компонент для защищенных роутов
const ProtectedRoute = ({ children, isAuthenticated, isCheckingAuth }) => {
    if (isCheckingAuth) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-slate-600">Проверка авторизации...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [toast, setToast] = useState(null);

    const checkAuthentication = async () => {
        setIsCheckingAuth(true);
        console.log('🔄 Начало проверки авторизации...');
        
        /**
         * Проверяем авторизацию напрямую через API
         * Куки с httponly=True недоступны через document.cookie,
         * но автоматически отправляются с запросом благодаря credentials: 'include'
         */
        const isValid = await verifyAuth();
        console.log('✅ Результат проверки:', isValid);
        setIsAuthenticated(isValid);
        
        setIsCheckingAuth(false);
        console.log('✅ Проверка авторизации завершена');
    };

    useEffect(() => {
        checkAuthentication();
    }, []);

    const handleLogin = async () => {
        // После логина проверяем авторизацию
        const isValid = await verifyAuth();
        setIsAuthenticated(isValid);
    };

    const handleLogout = async () => {
        try {
            // Вызываем API для удаления httponly cookie
            await fetch(`${API_BASE_URL}/api/auth/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
        } catch (error) {
            console.error('Ошибка при выходе:', error);
        }
        setIsAuthenticated(false);
    };

    const showToast = useCallback((msg) => {
        setToast(msg);
        setTimeout(() => setToast(null), 3000);
    }, []);

    return (
        <BrowserRouter>
            {toast && <Toast message={toast} onClose={() => setToast(null)} />}
            
            <Routes>
                <Route 
                    path="/login" 
                    element={
                        !isCheckingAuth && !isAuthenticated ? (
                            <LoginForm onLogin={handleLogin} />
                        ) : isAuthenticated ? (
                            <Navigate to="/" replace />
                        ) : (
                            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                                    <p className="mt-4 text-slate-600">Проверка авторизации...</p>
                                </div>
                            </div>
                        )
                    } 
                />
                <Route
                    path="/*"
                    element={
                        <ProtectedRoute 
                            isAuthenticated={isAuthenticated}
                            isCheckingAuth={isCheckingAuth}
                        >
                            <Layout
                                onLogout={handleLogout}
                                isMobileMenuOpen={isMobileMenuOpen}
                                setIsMobileMenuOpen={setIsMobileMenuOpen}
                            >
                                <Routes>
                                    <Route path="/" element={<Dashboard />} />
                                    <Route path="/orders" element={<OrdersPage onExport={() => showToast('Экспорт выполнен!')} />} />
                                    <Route path="/referrals" element={<ReferralsPage />} />
                                    <Route path="/partner-bots" element={<PartnerBotsPage />} />
                                    <Route path="/partner-bots/:id" element={<PartnerBotDetailPage />} />
                                    <Route path="/prices" element={<PricesPage onShowToast={showToast} />} />
                                    <Route path="/site-content" element={<SiteContentPage onShowToast={showToast} />} />
                                    <Route path="/digital-goods" element={<DigitalGoodsPage onShowToast={showToast} />} />
                                    <Route path="/settings" element={<Navigate to="/prices" replace />} />
                                    <Route path="/bot-settings" element={<BotSettingsPage onShowToast={showToast} />} />
                                    <Route path="/loyalty" element={<LoyaltySettingsPage onShowToast={showToast} />} />
                                    <Route path="/logs" element={<LogsPage />} />
                                    <Route path="/metrics" element={<MetricsPage />} />
                                    <Route path="/analytics" element={<AnalyticsPage />} />
                                    <Route path="/users/:id" element={<UserProfilePage onShowToast={showToast} />} />
                                    <Route path="/clients" element={<ClientsPage onShowToast={showToast} />} />
                                    <Route path="/anti-fraud" element={<AntiFraudPage onShowToast={showToast} />} />
                                    <Route path="/admin-users" element={<AdminUsersPage onShowToast={showToast} />} />
                                    <Route path="/roles" element={<RolesPage onShowToast={showToast} />} />
                                    <Route path="/reviews" element={<ReviewsPage onShowToast={showToast} />} />
                                    <Route path="/broadcasts" element={<BroadcastsPage onShowToast={showToast} />} />
                                    <Route path="/withdrawals" element={<WithdrawalsPage />} />
                                    <Route path="/frog" element={<FrogPage onShowToast={showToast} />} />
                                    <Route path="/frog2" element={<FrogPage onShowToast={showToast} usePayWrapper />} />
                                    <Route path="*" element={<NotFoundPage />} />
                                </Routes>
                            </Layout>
                        </ProtectedRoute>
                    }
                />
            </Routes>
        </BrowserRouter>
    );
}

export default App;
